import React, { useState, useEffect, useRef } from 'react';
import { Marker } from 'react-leaflet';
import L from 'leaflet';
import { Campsite, ReviewEntry } from '../types';
import { fetchReviews } from '../utils/api';

const customIcon = new L.Icon({
  iconUrl: '../leaflet/marker-icon.svg',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowUrl: '../leaflet/marker-shadow.svg',
  shadowSize: [41, 41]
});

interface SiteMarkerProps {
  site: Campsite;
  onSelect: (site: Campsite) => void;
  onAddReview: (site: Campsite) => void;
  isSelected?: boolean;
}

const SiteMarker: React.FC<SiteMarkerProps> = ({ site, onSelect, onAddReview, isSelected = false }) => {
  const [reviews, setReviews] = useState<ReviewEntry[]>([]);
  const [reviewsLoaded, setReviewsLoaded] = useState(false);
  const markerRef = useRef<L.Marker>(null);

  const lat = parseFloat(site.location?.lat);
  const lng = parseFloat(site.location?.lng);

  // Apply highlighting class to marker icon when selected
  useEffect(() => {
    if (markerRef.current) {
      const markerElement = markerRef.current.getElement();
      if (markerElement) {
        if (isSelected) {
          markerElement.classList.add('highlighted-marker');
        } else {
          markerElement.classList.remove('highlighted-marker');
        }
      }
    }
  }, [isSelected]);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    console.warn("Skipping invalid marker:", site);
    return null;
  }

  const handleMarkerClick = async () => {
    let currentReviews = reviews;

    if (site.ID && !reviewsLoaded) {
      try {
        // Fetch external reviews
        const externalReviews = await fetchReviews(site.ID);

        // Fetch local reviews
        const response = await fetch('/api/doggle-reviews');
        const allLocalReviews: any[] = await response.json();
        const localReviews = allLocalReviews.filter(r => String(r.siteID) === String(site.ID));

        // Merge: local first
        currentReviews = [...localReviews, ...externalReviews];
        setReviews(currentReviews);
        setReviewsLoaded(true);
      } catch (error) {
        console.error("Error fetching reviews for site:", site.ID, error);
      }
    }

    // Select the site to show in sidebar
    onSelect({ ...site, reviews: currentReviews });
  };

  return (
    <Marker
      ref={markerRef}
      position={[lat, lng]}
      icon={customIcon}
      eventHandlers={{
        click: handleMarkerClick
      }}
    />
  );
};

export default SiteMarker;